/**
 * Shared account menu — single source for Více overflow and account destinations.
 * Wave D — platform chrome SSOT (`/account/**`, `/orders/find`).
 *
 * FR-01 V1A (browse ON): bottom bar stays exactly Nakupovat|Skenovat|Objednávky.
 * Oblíbené / Obchody are NOT V1A primaries — reachability is More drawer + desktop sidebar
 * when `showFavorites` / `showShops` (env flags) are true (FR-02).
 *
 * V1B (browse OFF + scan ON): bar ORDER Oblíbené|Skenovat|Objednávky|Obchody
 * (shops-append). Same flags; More still lists them when not already a primary.
 *
 * Oblíbené (`favoritesHref` from `resolveShopHref`) is prepended in `buildAccountOverflowMenuItems`
 * when `showFavorites === true`. Obchody (`/shops`) follows when `showShops === true`.
 * Neither is mapped in `resolveOverflowNavDestination` so browse-on 4th-slot promotion
 * stays on kupóny + gifts + find-order only (T9) — never Oblíbené/Obchody on V1A bar.
 */

import { PLATFORM_SHOPS_PATH } from '../shop/resolveShopHref.js';

export interface AccountMenuItem {
  readonly to: string;
  readonly label: string;
  readonly description: string;
  /** True for the Přehled (account home) entry only. */
  readonly end?: boolean;
}

export interface AccountMenuDonationVisibility {
  readonly donate: boolean;
  readonly recurring: boolean;
  readonly myDonations: boolean;
}

export interface BuildAccountMenuItemsOptions {
  /** Kupóny / account-home overview — env `VITE_CUSTOMER_KUPONY_ENABLED`. */
  readonly showKupony?: boolean;
  /**
   * Optional pickup UX — Najít objednávku. Defaults false (deny-by-default) so
   * callers must pass `isBlockEntitled('order_pickup_infrastructure')`.
   */
  readonly showFindOrder?: boolean;
  /**
   * Obchody /shops — env `VITE_CUSTOMER_SHOPS_NAV_ENABLED`
   * (More + sidebar on V1A; V1B shops-append primary when browse OFF).
   */
  readonly showShops?: boolean;
  /**
   * Oblíbené — env `VITE_CUSTOMER_FAVORITES_NAV_ENABLED`
   * (More on V1A when not bar primary; V1B Oblíbené-first primary when browse OFF).
   */
  readonly showFavorites?: boolean;
  /** Resolved Oblíbené href — `resolveShopHref` → `/favorites` (guest and signed-in). */
  readonly favoritesHref?: string;
}

const PLATFORM_ACCOUNT_BASE = '/account';

/** Destinations owned by bottom-nav tabs — excluded from Více overflow. */
export function bottomNavAccountPaths(tenantCode: string): readonly string[] {
  void tenantCode;
  return [
    `${PLATFORM_ACCOUNT_BASE}/shop`,
    `${PLATFORM_ACCOUNT_BASE}/orders`,
  ];
}

export function buildAccountMenuItems(
  tenantCode: string,
  t: (key: string) => string,
  donationVisibility: AccountMenuDonationVisibility,
  options: BuildAccountMenuItemsOptions = {},
): readonly AccountMenuItem[] {
  void tenantCode;
  const showKupony = options.showKupony === true;
  const showFindOrder = options.showFindOrder === true;
  const accountBase = PLATFORM_ACCOUNT_BASE;
  const items: AccountMenuItem[] = [];
  if (showKupony) {
    items.push({
      // Kupóny + promo events live on AccountHomePage (index). Must stay reachable from Více.
      to: accountBase,
      label: t('account.menu.home.label'),
      description: t('account.menu.home.description'),
    });
  }
  if (donationVisibility.donate) {
    items.push({
      to: `${accountBase}/donate`,
      label: t('hub.donate.label'),
      description: t('account.menu.donations.description'),
    });
  }
  // Shop / Objednávky are bottom-nav primaries — always included so overflow
  // filtering can reserve their paths; never gate on product_vending.
  items.push({
    to: `${accountBase}/shop`,
    label: t('account.menu.shop.label'),
    description: t('account.menu.shop.description'),
  });
  if (donationVisibility.recurring) {
    items.push({
      to: `${accountBase}/recurring-donations`,
      label: t('account.menu.recurring.label'),
      description: t('account.menu.recurring.description'),
    });
  }
  if (donationVisibility.myDonations) {
    items.push({
      to: `${accountBase}/donations`,
      label: t('account.menu.donations.label'),
      description: t('account.menu.donations.description'),
    });
  }
  items.push({
    to: `${accountBase}/orders`,
    label: t('account.menu.orders.label'),
    description: t('account.menu.orders.description'),
  });
  // Najít objednávku — optional pickup infra (route guard remains defense in depth).
  if (showFindOrder) {
    items.push({
      to: '/orders/find',
      label: t('hub.orders.label'),
      description: t('hub.orders.description'),
    });
  }
  // Účet a soukromí lives on the shell profile menu (CustomerProfileSheet), not Více.
  return items;
}

/** Více drawer — everything except bottom-nav tab destinations. */
export function buildAccountOverflowMenuItems(
  tenantCode: string,
  t: (key: string) => string,
  donationVisibility: AccountMenuDonationVisibility,
  options: BuildAccountMenuItemsOptions = {},
): readonly AccountMenuItem[] {
  const reserved = new Set(bottomNavAccountPaths(tenantCode));
  const accountOverflow = buildAccountMenuItems(tenantCode, t, donationVisibility, options).filter(
    (item) => !reserved.has(item.to) && item.end !== true,
  );
  const prefix: AccountMenuItem[] = [];
  if (options.showFavorites === true && typeof options.favoritesHref === 'string') {
    prefix.push({
      to: options.favoritesHref,
      label: t('nav.shop'),
      description: t('account.shop.favoritesDescription'),
    });
  }
  if (options.showShops === true) {
    // T9/A7 — public Obchody; More+sidebar on V1A (FR-01 never bar primary);
    // V1B shops-append primary when browse OFF (ORDER …|Obchody).
    prefix.push({
      to: PLATFORM_SHOPS_PATH,
      label: t('nav.shops'),
      description: t('account.menu.browseShops.description'),
    });
  }
  if (prefix.length === 0) {
    return accountOverflow;
  }
  return [...prefix, ...accountOverflow];
}
